import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { WarehouseHandlingTier } from 'src/types/WarehouseHandlingTier';
import { PC_ADDED_EVENT } from '../../components/hierarchicalTable/GSLAHierarchicalTableAdapter';
import { usePCForWarehouse } from 'src/core/hooks/usePCForWarehouse';
import { APIErrorBoundary } from 'src/core/components/APIErrorBoundary';
import 'src/core/components/APIErrorBoundary.css';

interface AddProductionCapabilityProps {
    // Existing props - maintained for backward compatibility
    existingPCs: string[];
    onAdd: (pcName: string, warehouseHandlingTier: string) => Promise<boolean | void>;
    userCanEdit: boolean;
    availableTiers?: string[];
    
    // New props for API integration
    warehouseIds?: string[];  // Warehouses to query for PCs via API
    enableAPIMode?: boolean;  // Enable dropdown mode with API data (default: true)
    fallbackToTextInput?: boolean;  // Allow fallback to text input on API failure (default: true)
    showErrorDetails?: boolean;  // Show detailed error messages (default: false)
    
    // Advanced configuration
    apiRetryCount?: number;  // Number of API retry attempts (default: 2)
    cacheEnabled?: boolean;  // Enable React Query caching (default: true)
    
    // UI customization
    dropdownPlaceholder?: string;  // Custom placeholder for dropdown
    textInputPlaceholder?: string;  // Custom placeholder for text input
    loadingMessage?: string;  // Custom loading message
    
    // Event callbacks
    onAPIError?: (error: Error) => void;  // Called when API fails
    onModeSwitch?: (mode: 'dropdown' | 'text') => void;  // Called when switching modes
    
    // Default_Network_PC restriction support
    warehouseId?: string;  // Current warehouse context for Default_Network_PC restrictions
}

// Type for component modes
export type InputMode = 'dropdown' | 'text' | 'error';

// Type for component state
export interface ComponentState {
    currentMode: InputMode;
    isLoadingPCs: boolean;
    apiError: Error | null;
    availablePCs: string[];
    isAdding: boolean;
    pcName: string;
    selectedTier: string;
    isProcessing: boolean;
    error: string | null;
}

/**
 * Component for adding new Production Capabilities
 * Now supports both API-driven dropdown mode and text input fallback
 */
const AddProductionCapabilityCore: React.FC<AddProductionCapabilityProps> = ({
    // Existing props
    existingPCs,
    onAdd,
    userCanEdit,
    availableTiers = Object.values(WarehouseHandlingTier),
    
    // New API integration props
    warehouseIds = [],
    enableAPIMode = true,
    fallbackToTextInput = true,
    showErrorDetails = false,
    apiRetryCount = 2,
    cacheEnabled = true,
    
    // UI customization props
    dropdownPlaceholder = "Select a Production Capability",
    textInputPlaceholder = "Enter a Production Capability",
    loadingMessage = "Loading Production Capabilities...",
    
    // Event callbacks
    onAPIError,
    onModeSwitch,
    
    // Default_Network_PC restriction support
    warehouseId,
}) => {
    // Existing state
    const [isAdding, setIsAdding] = useState(false);
    const [pcName, setPcName] = useState('');
    const [selectedTier, setSelectedTier] = useState<string>(WarehouseHandlingTier.Default);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // New state for API integration
    const [currentMode, setCurrentMode] = useState<InputMode>('dropdown');
    const [showRetryOption, setShowRetryOption] = useState(false);
    
    // API integration - fetch production capabilities
    const shouldUseAPI = enableAPIMode && warehouseIds.length > 0;
    const {
        availablePCs: apiPCs,
        isLoading: isLoadingPCs,
        error: apiError,
        isError: hasAPIError,
        refetch: retryAPI,
    } = usePCForWarehouse(warehouseIds, shouldUseAPI && cacheEnabled);
    
    // Determine current mode based on API state and user preferences
    const effectiveMode = useMemo((): InputMode => {
        if (!shouldUseAPI) return 'text';
        if (hasAPIError && !fallbackToTextInput) return 'error';
        if (hasAPIError && fallbackToTextInput && currentMode === 'text') return 'text';
        if (hasAPIError) return 'error';
        return 'dropdown';
    }, [shouldUseAPI, hasAPIError, fallbackToTextInput, currentMode]);
    
    // Handle API errors
    useEffect(() => {
        if (hasAPIError && apiError) {
            setShowRetryOption(true);
            if (onAPIError) {
                onAPIError(apiError);
            }
        }
    }, [hasAPIError, apiError, onAPIError]);
    
    // Handle mode switches
    useEffect(() => {
        if (effectiveMode !== currentMode) {
            setCurrentMode(effectiveMode);
            if (onModeSwitch) {
                onModeSwitch(effectiveMode === 'error' ? 'text' : effectiveMode);
            }
        }
    }, [effectiveMode, currentMode, onModeSwitch]);

    const retryAPICall = useCallback(async () => {
        setShowRetryOption(false);
        setError(null);
        try {
            await retryAPI();
            setCurrentMode('dropdown');
        } catch (err) {
            setShowRetryOption(true);
        }
    }, [retryAPI]);
    
    // Enhanced validation that works with both dropdown and text input
    const validatePCName = useCallback((name: string): string | null => {
        // Check if empty
        if (!name.trim()) {
            return 'Production Capability name cannot be empty';
        }
        
        // Check for duplicates in existing PCs
        if (existingPCs.some(pc => pc.toLowerCase() === name.toLowerCase())) {
            return 'Production Capability name already exists';
        }
        
        // Default_Network_PC restriction - prevent adding when not on DEFAULT warehouse
        const isDefaultNetworkPC = name === 'Default_Network_PC';
        const canAddDefaultNetworkPC = !isDefaultNetworkPC || (warehouseId === 'DEFAULT');
        if (!canAddDefaultNetworkPC) {
            return 'Default_Network_PC can only be added when viewing DEFAULT warehouse pages';
        }
        
        // For text input mode, check for invalid characters
        if (effectiveMode === 'text' && !/^[a-zA-Z0-9_]+$/.test(name)) {
            return 'Production Capability name can only contain letters, numbers, and underscores';
        }
        
        return null;
    }, [existingPCs, effectiveMode, warehouseId]);
    
    // Get the appropriate PC options based on current mode
    const availablePCOptions = useMemo(() => {
        if (effectiveMode === 'dropdown' && apiPCs.length > 0) {
            // Filter out existing PCs from API results
            let filteredPCs = apiPCs.filter(pc =>
                !existingPCs.some(existing => existing.toLowerCase() === pc.toLowerCase())
            );
            
            // Default_Network_PC restriction - filter out when not on DEFAULT warehouse
            const isDefaultNetworkPCRestricted = warehouseId && warehouseId !== 'DEFAULT';
            if (isDefaultNetworkPCRestricted) {
                filteredPCs = filteredPCs.filter(pc => pc !== 'Default_Network_PC');
            }
            
            return filteredPCs;
        }
        return [];
    }, [effectiveMode, apiPCs, existingPCs, warehouseId]);

    // Handle the Add button click
    const handleAddClick = useCallback(async () => {
        // Validate the PC name first
        const validationError = validatePCName(pcName);
        if (validationError) {
            setError(validationError);
            return;
        }
        
        // Clear any previous errors
        setError(null);
        
        // Set processing state to prevent multiple clicks
        setIsProcessing(true);
        
        try {
            // Call the add handler with both pcName and selectedTier
            await onAdd(pcName, selectedTier);
            
            // Dispatch a custom event to notify other components that a PC was added
            const pcAddedEvent = new CustomEvent(PC_ADDED_EVENT, {
                detail: {
                    pcName: pcName,
                    tierName: selectedTier
                }
            });
            window.dispatchEvent(pcAddedEvent);
            
            // Reset UI state
            setIsAdding(false);
            setPcName('');
            setSelectedTier(WarehouseHandlingTier.Default);
        } catch (err) {
            setError('Failed to add Production Capability');
            console.error('Error adding PC:', err);
        } finally {
            // Reset processing state
            setIsProcessing(false);
        }
    }, [pcName, selectedTier, onAdd, validatePCName]);

    // Handle cancel click
    const handleCancelClick = useCallback(() => {
        setIsAdding(false);
        setPcName('');
        setError(null);
    }, []);

    // Early return if user can't edit
    if (!userCanEdit) {
        return null;
    }

    // If we're not in adding mode, display the button to start adding
    if (!isAdding) {
        return (
            <div className="add-pc-container">
                <button 
                    className="add-pc-button"
                    onClick={() => setIsAdding(true)}
                >
                    <span className="add-icon">+</span> Select Production Capability
                </button>
            </div>
        );
    }

    // Display the PC input form when in adding mode
    return (
        <div className="add-pc-form">
            <div className="form-container">
                {/* Loading state */}
                {isLoadingPCs && effectiveMode === 'dropdown' && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <span>{loadingMessage}</span>
                    </div>
                )}

                {/* Error state with retry options */}
                {effectiveMode === 'error' && (
                    <div className="error-state">
                        <div className="error-message api-error">
                            <strong>Unable to load production capabilities</strong>
                            {showErrorDetails && apiError && (
                                <div className="error-details">
                                    {apiError.message}
                                </div>
                            )}
                        </div>
                        <div className="error-actions">
                            {showRetryOption && (
                                <button
                                    className="retry-button"
                                    onClick={retryAPICall}
                                    disabled={isLoadingPCs}
                                >
                                    {isLoadingPCs ? 'Retrying...' : 'Retry'}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Main input section - only show when not in pure error state */}
                {effectiveMode !== 'error' && (
                    <>
                        {/* Production Capability Input */}
                        <div className="input-group">
                            <label htmlFor="pc-input">Production Capability:</label>
                            
                            {/* Dropdown mode */}
                            {effectiveMode === 'dropdown' && !isLoadingPCs && (
                                <>
                                    <select
                                        id="pc-input"
                                        className="pc-dropdown"
                                        value={pcName}
                                        onChange={(e) => setPcName(e.target.value)}
                                        autoFocus
                                    >
                                        <option value="">{dropdownPlaceholder}</option>
                                        {availablePCOptions.map(pc => (
                                            <option key={pc} value={pc}>{pc}</option>
                                        ))}
                                    </select>
                                    
                                    
                                    {/* Show available count */}
                                    {availablePCOptions.length > 0 && (
                                        <div className="pc-count-info">
                                            {availablePCOptions.length} production capabilities available
                                        </div>
                                    )}
                                    
                                    {/* Show message if no options available */}
                                    {availablePCOptions.length === 0 && apiPCs.length > 0 && (
                                        <div className="no-options-message">
                                            All available production capabilities are already added
                                        </div>
                                    )}
                                </>
                            )}
                            
                            {/* Text input mode */}
                            {effectiveMode === 'text' && (
                                <>
                                    <input
                                        type="text"
                                        id="pc-input"
                                        className="pc-input"
                                        placeholder={textInputPlaceholder}
                                        value={pcName}
                                        onChange={(e) => setPcName(e.target.value)}
                                        autoFocus
                                    />
                                    
                                    {/* Option to switch back to dropdown if API is working */}
                                    {shouldUseAPI && !hasAPIError && availablePCOptions.length > 0 && (
                                        <button
                                            type="button"
                                            className="mode-switch-button"
                                            onClick={() => setCurrentMode('dropdown')}
                                            title="Select from available production capabilities"
                                        >
                                            Select from list
                                        </button>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Handling Tier Selection */}
                        <div className="input-group">
                            <label htmlFor="tier-select">Warehouse Handling Tier:</label>
                            <select
                                id="tier-select"
                                value={selectedTier}
                                onChange={(e) => setSelectedTier(e.target.value)}
                                className="tier-select"
                            >
                                {availableTiers.map(tier => (
                                    <option key={tier} value={tier}>{tier}</option>
                                ))}
                            </select>
                        </div>

                        {/* Action Buttons */}
                        <div className="button-group">
                            <button
                                className="add-button save-button"
                                disabled={!pcName.trim() || isProcessing}
                                onClick={handleAddClick}
                            >
                                {isProcessing ? 'Adding...' : 'Add'}
                            </button>
                            <button
                                className="cancel-button"
                                onClick={handleCancelClick}
                            >
                                Cancel
                            </button>
                        </div>

                        {/* Validation Error Display */}
                        {error && (
                            <div className="error-message validation-error">
                                {error}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * AddProductionCapability component wrapped with API Error Boundary
 * Provides graceful error handling for API failures
 */
export const AddProductionCapability: React.FC<AddProductionCapabilityProps> = (props) => {
    return (
        <APIErrorBoundary
            onError={(error, errorInfo) => {
                console.error('AddProductionCapability Error:', {
                    error: error.message,
                    stack: error.stack,
                    componentStack: errorInfo.componentStack,
                    warehouseIds: props.warehouseIds,
                    enableAPIMode: props.enableAPIMode,
                    timestamp: new Date().toISOString(),
                });
                
                // Call the user's onAPIError callback if provided
                if (props.onAPIError) {
                    props.onAPIError(error);
                }
            }}
            resetOnPropsChange={true}
            resetKeys={[
                props.warehouseIds?.join(',') || '',
                props.enableAPIMode?.toString() || 'false',
                props.existingPCs.length.toString()
            ]}
            fallback={
                <div className="add-pc-container">
                    <div className="api-error-fallback">
                        <p>Unable to load production capabilities. Please try refreshing the page.</p>
                        <button
                            className="add-pc-button"
                            onClick={() => window.location.reload()}
                        >
                            <span className="add-icon">↻</span> Refresh Page
                        </button>
                    </div>
                </div>
            }
        >
            <AddProductionCapabilityCore {...props} />
        </APIErrorBoundary>
    );
};
